import { defineStore } from 'pinia';
import apiClient from '@/util/apiClient';

export const useComplexMapStore = defineStore('complexMapStore', {
	state: () => ({
		_byLocation: new Map()
	}),

	actions: {
		/**
		 * Fetches complex-map data for a location, using cache by default.
		 *
		 * @param {number} locationId
		 * @param {Object} options
		 * @param {boolean} options.force bypass in-memory cache when true
		 * @returns {Object|null} complex map payload or null when unavailable
		 */
		async fetchByLocation(locationId, { force = false } = {}) {
			if (!force && this._byLocation.has(locationId)) {
				return this._byLocation.get(locationId);
			}
			const { data } = await apiClient.get(`/locations/${locationId}/complex-map`);
			if (data) this._byLocation.set(locationId, data);
			return data || null;
		},

		/**
		 * Creates a waypoint for a location and invalidates that location's map cache.
		 *
		 * @param {number} locationId
		 * @param {Object} payload waypoint create payload
		 * @returns {Object} created waypoint DTO
		 */
		async createWaypoint(locationId, payload) {
			const { data } = await apiClient.post(`/locations/save/${locationId}/waypoints`, payload);
			this._byLocation.delete(locationId);
			return data;
		},

		/**
		 * Updates an existing waypoint and invalidates that location's map cache.
		 *
		 * @param {number} locationId
		 * @param {number} waypointId
		 * @param {Object} payload waypoint patch payload
		 * @returns {Object} updated waypoint DTO
		 */
		async updateWaypoint(locationId, waypointId, payload) {
			const { data } = await apiClient.patch(`/locations/edit/${locationId}/waypoints/${waypointId}`, payload);
			this._byLocation.delete(locationId);
			return data;
		},

		/**
		 * Deletes a waypoint and invalidates that location's map cache.
		 *
		 * @param {number} locationId
		 * @param {number} waypointId
		 */
		async deleteWaypoint(locationId, waypointId) {
			await apiClient.delete(`/locations/delete/${locationId}/waypoints/${waypointId}`);
			this._byLocation.delete(locationId);
		},

		/**
		 * Manually invalidates cached complex-map data for a location.
		 *
		 * @param {number} locationId
		 */
		invalidate(locationId) {
			this._byLocation.delete(locationId);
		}
	}
});
